import { withBxApiContext } from 'helpers/withBxApiContext';
import bxApi from 'services/bxApi';

import { ROUTE_TYPES } from 'app/(clickouts)/constants';
import {
  BuyerIntentCompetitorsView,
  getBuyerIntentCategoryId,
  getBuyerIntentProductId,
  warnMissingBuyerIntentIds,
} from 'components/BuyerIntent';
import { removeQueryParams } from 'helpers/SEOHelper';
import { updateBaseUrl } from 'helpers/bxApi';
import {
  applySelectedFacets,
  filterAlternatives,
  transformAlternativesData,
  updateFacetCounts,
} from 'helpers/dataTransformers/alternativesBxApi';
import { generateAlternativesPageHead } from 'helpers/dataTransformers/alternativesPageHead';
import { getGeoipDetails } from 'helpers/geoipDetector';
import { getAppData, setCacheTags } from 'helpers/initializePage';
import hasALiveChatCategory, { hasPplCategory } from 'helpers/liveChat';
import sortAlternatives from 'helpers/sortAlternatives';
import { ROUTE_NAMES } from 'routes';
import Alternatives from 'screens/alternatives';
import initializeExperiments from 'services/launchDarkly';
import { getPplCategories } from 'services/pplCategories/pplCategories';
import publishedPagesService from 'services/publishedPages';
import routeService from 'services/routes';
Alternatives.ddogName = 'alternatives';
export const getServerSideProps = withBxApiContext(
  'alternatives',
  async (ctx) => {
    const { url } = ctx.req;

    let results;

    // Facets redirect check (previously in backend: facets_redirect_check)
    // Redirect freemium → free and iphone-ipad → ipad
    const { pricingcategories_slug, oses_slug } = ctx.query;

    // Check for freemium pricing category
    if (pricingcategories_slug) {
      const shouldRedirectFreemium =
        pricingcategories_slug === 'freemium' ||
        (Array.isArray(pricingcategories_slug) &&
          pricingcategories_slug.includes('freemium'));

      if (shouldRedirectFreemium) {
        const newPricingSlug = Array.isArray(pricingcategories_slug)
          ? pricingcategories_slug.map((s) => (s === 'freemium' ? 'free' : s))
          : 'free';

        const newQuery = {
          ...ctx.query,
          pricingcategories_slug: newPricingSlug,
        };
        const queryString = new URLSearchParams(newQuery).toString();
        const redirectPath = `${ctx.req.url.split('?')[0]}?${queryString}`;

        return {
          redirect: {
            destination: redirectPath,
            statusCode: 301,
          },
        };
      }
    }

    // Check for iphone-ipad OS
    if (oses_slug) {
      const shouldRedirectIphoneIpad =
        oses_slug === 'iphone-ipad' ||
        (Array.isArray(oses_slug) && oses_slug.includes('iphone-ipad'));

      if (shouldRedirectIphoneIpad) {
        const newOsesSlug = Array.isArray(oses_slug)
          ? oses_slug.map((s) => (s === 'iphone-ipad' ? 'ipad' : s))
          : 'ipad';

        const newQuery = { ...ctx.query, oses_slug: newOsesSlug };
        const queryString = new URLSearchParams(newQuery).toString();
        const redirectPath = `${ctx.req.url.split('?')[0]}?${queryString}`;

        return {
          redirect: {
            destination: redirectPath,
            statusCode: 301,
          },
        };
      }
    }

    const cleanedUrl = removeQueryParams(url, [
      'tag_slug',
      'pricingcategories_slug',
      'oses_slug',
      'rating_stars_ge',
      'typecustomers_slug',
      'integrated_with_slug',
      'community_company_size',
      'community_company_industry',
    ]);
    if (cleanedUrl !== url) {
      return {
        redirect: {
          destination: cleanedUrl,
          statusCode: 301,
        },
      };
    }

    const { countryCode } = getGeoipDetails(ctx);

    try {
      results = await Promise.all([
        bxApi.getAlternativesHreflangsBySlug(ctx.query.slug).catch(() => []),
        initializeExperiments({
          ctx,
          pageType: ROUTE_TYPES.ALTERNATIVES,
        }),
        bxApi.getAlternativesBySlug(countryCode, {
          slug: ctx.query.slug,
          categorySelection: ['HIGHEST_CPC_DIRECTORY', 'PRIMARY'],
          relatedProductsCategorySelection: [
            'HIGHEST_CPC_DIRECTORY',
            'PRIMARY',
          ],
        }),
        publishedPagesService.getPublishedPage(ctx.query.slug),
        bxApi.findPublishedComparisonsBySlug(countryCode, ctx.query.slug),
        getPplCategories({ countryCode }),
      ]);
    } catch (error) {
      return {
        props: {
          appData: { ...getAppData(ctx, {}), hreflangs: [], url },
          error: { statusCode: error.statusCode || 500 },
        },
      };
    }

    const [
      hreflangs,
      experiments,
      bxProduct,
      publishedPages,
      popularComparisons,
      pplCategories,
    ] = results;

    // Check if alternatives page is not published - redirect to listing detail
    const isCypressTakingSnapshots =
      ctx.req.headers['fe-cypress-taking-snapshots'] === '0';
    if (
      !(isCypressTakingSnapshots || publishedPages[ROUTE_NAMES.ALTERNATIVES])
    ) {
      const publishedPagesDestination =
        publishedPages?.destination &&
        updateBaseUrl(publishedPages?.destination);

      const fallbackRoute = routeService.generatePath(
        ROUTE_NAMES.LISTING_DETAILS,
        {
          slug: ctx.query.slug,
          header: ctx.query.header,
        }
      );
      return {
        redirect: {
          destination: publishedPagesDestination || fallbackRoute,
          statusCode: 301,
        },
      };
    }

    // Transform BX-API data into the shape expected by components
    const basePath = `/${ctx.query.header}-software/a/${ctx.query.slug}/alternatives/`;
    const {
      listing,
      alternatives: allAlternatives,
      facets: rawFacets,
    } = transformAlternativesData(bxProduct, basePath, ctx.query.header);

    // Build filter object from query params (extract filter values)
    const filters = {};
    const filterParams = [
      'pricingcategories_slug',
      'tag_slug',
      'rating_stars_ge',
      'typecustomers_slug',
      'oses_slug',
      'integrated_with_slug',
      'community_company_size',
      'community_company_industry',
    ];
    filterParams.forEach((param) => {
      const value = ctx.query[`${param}[]`] || ctx.query[param];
      if (value) {
        filters[param] = value;
      }
    });

    // Apply client-side filtering and sorting (premium first, then by rating)
    const alternatives = sortAlternatives(
      filterAlternatives(allAlternatives, filters)
    );

    // Update facet counts based on filtered alternatives
    const facetsWithCounts = updateFacetCounts(rawFacets, alternatives);

    // Apply selected state and proper hrefs to facets based on URL query params
    const facets = applySelectedFacets(facetsWithCounts, ctx.query, basePath);

    // Generate head metadata from BX-API data (after filtering to get correct count)
    const { head, breadcrumb, categorySlug } = generateAlternativesPageHead(
      ctx,
      bxProduct,
      ctx.query.header,
      alternatives.length
    );

    // Build tab URLs from published pages
    const tabUrls = publishedPages?.tab_urls || [];

    // Build pageData structure (without backend API)
    const pageData = {
      header: {
        breadcrumb,
        tab_urls: tabUrls,
      },
      head,
      route: ROUTE_TYPES.ALTERNATIVES,
      google_analytics: {
        content_group: ROUTE_TYPES.ALTERNATIVES,
      },
    };

    const appData = {
      ...getAppData(ctx, pageData),
      experiments,
      hreflangs,
      url,
    };

    // NEW TABS prepared from BXAPI
    if (publishedPages) {
      appData.newNavigationTabs = tabUrls
        .filter((tab) => !['integrations', 'features'].includes(tab.slug))
        .map((tab) => ({
          ...tab,
          label: tab.slug === 'app-info' ? 'Overview' : tab.label,
          active: tab.slug === 'alternatives',
        }));
    }

    // We pass in current Request this info to Document in order to avoid double calculation.
    ctx.req.APP_DATA = appData;
    setCacheTags(ctx, ROUTE_NAMES.ALTERNATIVES);

    const [{ geoipCountryCode } = {}] = appData.head.data_layer || [];

    const hasPplCategoryValue = hasPplCategory(pplCategories, categorySlug);
    appData.head.hasPplCategory = hasPplCategoryValue;
    appData.head.hasPplExitIntentEnabled =
      ['US', 'CA'].includes(geoipCountryCode) && hasPplCategoryValue;
    appData.head.hasLiveChatEnabled = hasALiveChatCategory(
      pplCategories,
      listing?.categories
    );

    return {
      props: JSON.parse(
        JSON.stringify({
          appData: { ...appData },
          alternativesData: {
            listing,
            popularComparisons,
            alternatives,
            facets,
            queryParams: {
              base_path: basePath,
            },
          },
        })
      ),
    };
  }
);

const AlternativesPage = (props) => {
  const productId = getBuyerIntentProductId({
    appData: props.appData,
    listing: props.alternativesData?.listing,
  });
  const categoryId = getBuyerIntentCategoryId(props.appData);

  if (!productId) {
    warnMissingBuyerIntentIds('page:alternatives', {
      productId: props.alternativesData?.listing?.global_product_id,
      categoryId,
    });

    return <Alternatives {...props} />;
  }

  return (
    <BuyerIntentCompetitorsView
      productId={productId}
      categoryId={categoryId}
      sourceLocation="page:alternatives"
    >
      <Alternatives {...props} />
    </BuyerIntentCompetitorsView>
  );
};

AlternativesPage.ddogName = Alternatives.ddogName;
AlternativesPage.Layout = Alternatives.Layout;
AlternativesPage.hasBuyerIntentPageView = true;

export default AlternativesPage;
